// Description: This component is responsible for rendering the templates page, where the user can see all the templates that he has created, and also can create new ones.
// Updated: 29/08/2025
// Author: Vitor de Jesus

// React
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Material UI Components
import AddCommentIcon from '@mui/icons-material/AddComment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ForumIcon from '@mui/icons-material/Forum';
import { Visibility } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';

// Components and Functions for the project
import DeleteIcon from '../../assets/Icons/components/Delete';
import DuplicateIcon from '../../assets/Icons/components/Duplicate';
import EditIcon from '../../assets/Icons/components/Edit';
import EmptyTemplate from "../../assets/ilustrations/EmptyTemplates.png"
import HeaderPage from '../../components/HeaderPage/HeaderPage';
import { isAuthenticated } from '../../authMiddleware';
import { GetAllTemplates } from '../../api/aws/Templates/GetAllTemplates';
import ModalR from '../../components/Modals/ModalR';
import StatusLabel from '../../components/StatusLabel/StatusLabel';
import StatusIcon from '../../components/StatusLabel/StatusIcon';
import { TemplateDelete } from '../../api/aws/Templates/TemplateDelete';
import { TemplateDuplicate } from '../../api/aws/Templates/TemplateDuplicate';
import { TemplateHidden } from '../../api/aws/Templates/TemplateHidden';
import Titles from '../../components/Titles/Titles';
import ThemeColors from '../../components/ThemeColors/ThemeColors';
import { notify, truncateText, updateMessageType } from '../../utils/utils';
import use123zapStore from '../../context/123zapStore';
import SheetsTable from '../../components/Tables/SheetsTable';
import themeColors from '../../components/ThemeColors/ThemeColors';

// Render The Page
function Templates() {

  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const userInfo = use123zapStore((state) => state.userInfo);
  const setUserInfo = use123zapStore((state) => state.setUserInfo);
  const templates = use123zapStore((state) => state.templates);
  const setTemplates = use123zapStore((state) => state.setTemplates);
  const alreadyFetchTemplates = use123zapStore((state) => state.alreadyFetchTemplates);
  const setAlreadyFetchTemplates = use123zapStore((state) => state.setAlreadyFetchTemplates);
  let Navigate = useNavigate();

  const [loading, setLoading] = useState(alreadyFetchTemplates === false ? true : false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [openModalConfirmDuplicate, setOpenModalConfirmDuplicate] = useState(false);

  const [dataCards, setDataCards] = useState([
    { title: 'Modelos Armazenados', dataKey: 'conectados', icon: ForumIcon, qtd: 0, },
    { title: 'Modelos Ativos', dataKey: 'desconectados', icon: AddCommentIcon, qtd: 0, }
  ]);

  useEffect(() => {
    if (userAuthenticated && Object.keys(userInfo).length === 0) {
      const { decryptedData } = isAuthenticated();
      setUserInfo(decryptedData);
    }
  }, []);

  useEffect(() => {
    if (userInfo['UUID'] && alreadyFetchTemplates === false) {
      fetchData();
    }
  }, [userInfo]);

  function getPreviewMessage(channelType, formattedMessageJson) {
    try {
      if (!channelType || !formattedMessageJson) return '';

      const messages = formattedMessageJson?.messages || [];

      switch (channelType) {
        case 'WhatsAppAPI':
        case 'WhatsAppWEB': {
          const textMessage = messages.find(
            (m) => String(m?.type).includes('whatsapp') && typeof m?.message === 'string' && m?.type !== 'img'
          );
          return textMessage?.message || '';
        }

        case 'SMS': {
          const firstMessage = messages.find(
            (m) => typeof m?.message === 'string'
          );
          return firstMessage?.message || '';
        }

        case 'E-mail': {
          const message = formattedMessageJson?.messages[0]?.message[0]?.content;

          return message || '';
        }

        default:
          return '';
      }
    } catch (error) {
      console.error('Erro ao extrair preview da mensagem:', error);
      return '';
    }
  }

  const fetchData = async () => {
    try {

      const resultJson = await GetAllTemplates();

      const items = resultJson.data.map(template => {
        return {
          id: template.id,
          title: template.title,
          integration_icons: template.channel_type,
          status: template.status,
          msg_type: template.msg_type,
          type: template.type,
          background: template.background,
          messageJson: template.message_json,
          Visibility: template.visibility ? 'Visível' : 'Oculto',
          message: getPreviewMessage(template.channel_type, template.channel_type === 'E-mail' ? template.message_json : updateMessageType(template.message_json))
        };
      }).filter(item => item !== null);

      const newDataCards = [...dataCards];

      newDataCards[0].qtd = items.length;
      newDataCards[1].qtd = items.filter(item => item.status === 'Ativo').length;

      setDataCards(newDataCards);

      const newItens = [items]

      setTemplates(...newItens);

    } catch (error) {
      console.error('Erro ao obter modelos:', error);
    } finally {
      setLoading(false);
      setAlreadyFetchTemplates(true);
    }
  };

  const alterVisibility = async (id_template, visibility) => {
    try {
      let data = { 'id_template': id_template, 'visibility': visibility };

      const templateData = await TemplateHidden(data);

      if (templateData.status === 'success') {

        fetchData();
      }

    } catch (error) {
      console.error('Erro ao obter dados da API:', error);

    }
  }

  const handleConfirmDelete = async () => {
    try {

      const resultJson = await TemplateDelete({ "id_template": selectedItem.id });

      if (resultJson.status === 'success') {
        const newItems = templates.filter(item => item.id !== selectedItem.id);
        setTemplates(newItems);
        notify("Success", "Modelo deletado com sucesso!", true);
      } else {
        alert('Erro ao excluir modelo. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao excluir modelo:', error);
    } finally {
      handleCloseModalConfirmDelete();
    }
  };

  const handleConfirmDuplicate = async () => {
    try {

      const resultJson = await TemplateDuplicate(selectedItem.id);

      if (resultJson.status === 'success') {
        fetchData();
        notify("Success", "Modelo duplicado com sucesso!", true);
      } else {
        alert('Erro ao duplicar modelo. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao duplicar modelo:', error);
    } finally {
      handleCloseModalConfirmDuplicate();
    }
  };

  const handleOpenPageAdd = () => {
    Navigate('/modelos/canal');
  };

  const handleOpenModalConfirmDelete = (item) => {
    setSelectedItem(item);
    setOpenModalConfirm(true);
  };

  const handleCloseModalConfirmDelete = () => {
    setOpenModalConfirm(false);
  }

  const handleOpenModalConfirmDuplicate = (item) => {
    setSelectedItem(item);
    setOpenModalConfirmDuplicate(true);
  }

  const handleCloseModalConfirmDuplicate = () => {
    setOpenModalConfirmDuplicate(false);
  }

  return (
    <Fragment>
      <Box width={"100%"} height={"100vh"}>
        < HeaderPage title="Modelos" />
        {
          loading === false && templates.length === 0 && (
            <Box display={"flex"} sx={{ justifyContent: "center", alignItems: "center", width: { xs: "80%", sm: "100%" }, height: "90%" }}>
              <Box sx={{ width: { xs: "80%", sm: "auto" } }}>
                <Box component="img" src={EmptyTemplate} alt="Você não tem modelos" sx={{ width: { xs: "247px", sm: "494px" }, height: { xs: "165px", sm: "330px" } }} />
                <Box display={"flex"} sx={{ flexDirection: "column", gap: "10px", justifyContent: "center", textAlign: "center" }}>
                  <Titles bold type={"H3"} children={"Parece que você não tem modelos :("} color={"#8033F4"} />
                  <Titles type={"P1"} children={"Crie um agora mesmo, é fácil e rápido"} />

                  <Button
                    variant='contained'
                    style={{ color: '#fff', width: '100%', padding: '10px', marginTop: "10px" }}
                    onClick={handleOpenPageAdd}
                  >
                    Criar Modelo
                  </Button>
                </Box>
              </Box>
            </Box>
          ) || (
            <SheetsTable
              tableName={'Modelos'}
              data={templates}
              columns={[
                { headerName: '', field: 'integration_icons', renderCell: (params) => <StatusIcon status={params.value} color={themeColors.purple500} />, width: 80 },
                { headerName: 'Nome', field: 'title', flex: 1 },
                { headerName: 'Tipo', field: 'msg_type', renderCell: (params) => <StatusLabel status={params.value} usingOnTable />, flex: 1 },
                {
                  headerName: 'Mensagem',
                  field: 'message',
                  renderCell: (params) => (
                    params.value !== '' && params.value !== null ? (
                      <Tooltip
                        title={
                          <Box
                            sx={{
                              backgroundColor: '#fff',
                              color: '#000',
                              padding: '10px',
                              borderRadius: '8px',
                              maxWidth: '350px',
                              whiteSpace: 'pre-wrap',
                              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
                              fontSize: '13px',
                            }}
                          >
                            {params.value}
                          </Box>
                        }
                        placement="top-start"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'transparent',
                              padding: 0,
                            },
                          },
                        }}
                      >
                        <Chip
                          label={truncateText(String(params.value), 30)}
                          variant="outlined"
                          sx={{
                            maxWidth: '100%',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            border: 'none',
                            fontSize: { xs: '11px', sm: '12px', lg: '14px', xl: '16px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label={'Não há prévia disponível'}
                        variant="outlined"
                        sx={{
                          maxWidth: '100%',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          border: 'none',
                          fontSize: { xs: '11px', sm: '12px', lg: '14px', xl: '16px' },
                        }}
                      />
                    )
                  ),
                  flex: 1
                },
                // { headerName: 'Canal', field: 'integration_icons', renderCell: (integration) => integrationIcons[integration] },
                { headerName: 'Status', field: 'status', renderCell: (params) => <StatusLabel status={params.value} usingOnTable />, flex: 1 },
              ]}
              actions={[
                { icon: <DuplicateIcon />, tooltip: 'Duplicar', action: (item) => handleOpenModalConfirmDuplicate(item), isVisible: (item) => true },
                { icon: <DeleteIcon />, tooltip: 'Deletar', action: (item) => handleOpenModalConfirmDelete(item), isVisible: (item) => true },
                {
                  icon: <EditIcon />, tooltip: 'Editar', action: (item) => {
                    localStorage.setItem('templateId', item.id);
                    if (
                      item.integration_icons === "WhatsApp" ||
                      item.integration_icons === "WhatsAppWEB" ||
                      item.integration_icons === "SMS") {
                      Navigate("/modelos/editar");
                    }
                    else if (item.integration_icons === "WhatsAppAPI") {
                      Navigate("/modelos/editar/whatsapp-api");
                    }
                    else {
                      Navigate("/modelos/email/editar");
                    }

                  }, isVisible: (item) => true
                },
                { icon: <Visibility />, tooltip: 'Ocultar', action: (item) => alterVisibility(item.id, false), isVisible: (item) => item && item.Visibility === 'Visível' },
                { icon: <Visibility />, tooltip: 'Exibir', action: (item) => alterVisibility(item.id, true), isVisible: (item) => item && item.Visibility === 'Oculto' }
              ]}

              redirect={handleOpenPageAdd}
              redirectText={"Adicionar Modelo"}
              loading={loading}
            />
          )
        }
      </Box>
      <ModalR
        cancelText={"Mudei de ideia"}
        color={ThemeColors.purple500}
        confirmText={"Sim, desejo excluir"}
        subTitle={"Esta ação não poderá ser desfeita"}
        isOpen={openModalConfirm}
        onClose={handleCloseModalConfirmDelete}
        onConfirm={handleConfirmDelete} // Chama a função de confirmação
        title={"Tem certeza que deseja excluir?"}
        emoji={"emoji10"}
        iconColor={ThemeColors.purple400}
      />
      <ModalR
        cancelText={"Mudei de ideia"}
        color={ThemeColors.purple500}
        confirmText={"Sim, desejo duplicar"}
        subTitle={""}
        isOpen={openModalConfirmDuplicate}
        onClose={handleCloseModalConfirmDuplicate}
        onConfirm={handleConfirmDuplicate}
        title={"Tem certeza que deseja duplicar?"}
        emoji={"emoji02"}
        iconColor={ThemeColors.purple400}
      />
    </Fragment>
  );
}

export default Templates;